import {
  ConstrainMode,
  DetailsList,
  IColumn,
  IContextualMenuItem,
  Icon,
  PrimaryButton,
  SelectionMode,
  Stack,
  Text,
  Toggle
} from "@fluentui/react";
import { MouseEvent, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import AccordionContractDetailRow from "../components/AccordionContractDetailRow";
import TagReplacementDialog from "../components/ItemReplacement/Tag/TagReplacementDialog";
import { ApprovalStep, Contract, ContractItem } from "../contract.interface";
import { ActionColumnConfig } from "../ProductColumns/actionsColumn";
import { copyAndSortProducts } from "../ProductColumns/helpers";

import { DetachInventoryModal } from "./DetachInventoryModal";
import { LineAdditionalServicesModal } from "./LineAdditionalServicesModal";
import { LinkInventoryModal } from "./LinkInventoryModal";

import { buildColumns } from "@/components/Contracts/ProductColumns";
import { Card } from "@/components/Shared/Card";
import { useApi } from "@/core/libs/api/react-query/useApi";
import { listStyles } from "@/core/libs/list-data/lib/styles";
import { ContractStatus } from "@/modules/Contracts/enums";
import { ServicesCategoriesTypesEnum } from "@/modules/Settings/types/ServicesCategories.types";
import { IStep } from "@/modules/Terms/interfaces/ITerm.interface";
import { BusinessUnit } from "@/modules/User/types";

const LINE_ADDITIONAL_SERVICES_LINE_NUMBER_PARAM =
  "lineAdditionalServicesLineNumber";

interface ProductInfoPropsApprovalFlow {
  steps: Array<ApprovalStep>;
}

export interface ProductInfoProps {
  items: Array<ContractItem>;
  contract: Contract;
  businessUnitId: number;
  onOpenRenewalProductsModal: () => void;
  businessUnit: BusinessUnit;
  service: number;
  isEditingEnabled?: boolean;
  nextStep?: IStep;
  currentStep?: IStep;
  approvalFlow?: ProductInfoPropsApprovalFlow;
  refetchContractData?: () => Promise<void>;
  defaultCompactMode?: boolean;
  serviceCategoryType?: ServicesCategoriesTypesEnum;
}

export function DisplayProducts(props: ProductInfoProps): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const locationSearchRef = useRef(location.search);
  const [isCompactMode, setIsCompactMode] = useState(
    props.defaultCompactMode || false
  );
  const [products, setProducts] = useState(props.items || undefined);
  const [columns, setColumns] = useState([]);
  const refProducts = useRef(products);
  const refColumns = useRef(columns);

  const [isLinkInventoryModalOpen, setIsLinkInventoryModalOpen] =
    useState(false);
  const [isDetachInventoryModalOpen, setIsDetachInventoryModalOpen] =
    useState(false);
  const [selectedItemForInventoryModal, setSelectedItemForInventoryModal] =
    useState<ContractItem>(null);

  const { execute } = useApi();

  const { items, isEditingEnabled, currentStep, approvalFlow } = props;
  const isSimExempt = Boolean(props.contract.activationInstallment?.exemptSim);

  const lineAdditionalServicesLineNumber = new URLSearchParams(
    location.search
  ).get(LINE_ADDITIONAL_SERVICES_LINE_NUMBER_PARAM);

  const normalizedLineAdditionalServicesLineNumber =
    lineAdditionalServicesLineNumber?.replace(/\D/g, "");

  const selectedItemForLineAdditionalServices =
    normalizedLineAdditionalServicesLineNumber
      ? items.find(
          item =>
            item.line?.number?.replace(/\D/g, "") ===
            normalizedLineAdditionalServicesLineNumber
        )
      : null;

  const isLineAdditionalServicesModalOpen = Boolean(
    selectedItemForLineAdditionalServices
  );

  locationSearchRef.current = location.search;

  const serviceDeliveredStepOrder = approvalFlow?.steps?.find(
    step => step.internalName === ContractStatus.SERVICE_DELIVERED
  )?.order;
  const productDeliveredStepOrder = approvalFlow?.steps?.find(
    step => step.internalName === ContractStatus.PRODUCT_DELIVERED
  )?.order;

  const serviceCategoryIsTag =
    props.serviceCategoryType === ServicesCategoriesTypesEnum.TAG;

  const updateProducts = updateProducts => {
    refProducts.current = updateProducts;
    setProducts(updateProducts);
  };

  const updateColumns = updateColumns => {
    refColumns.current = updateColumns;
    setColumns(updateColumns);
  };

  const onColumnClick = (
    event: MouseEvent<HTMLElement>,
    column: IColumn
  ): void => {
    let sortedItems = refProducts.current;
    let isSortedDescending = column.isSortedDescending;

    // If we've sorted this column, flip it.
    if (column.isSorted) {
      isSortedDescending = !isSortedDescending;
    }

    // Sort the items.
    sortedItems = copyAndSortProducts(
      sortedItems,
      column.fieldName,
      isSortedDescending
    );

    // Reset the items and columns to match the state.
    updateProducts(sortedItems);

    updateColumns(
      refColumns.current.map(col => {
        col.isSorted = col.key === column.key;

        if (col.isSorted) {
          col.isSortedDescending = isSortedDescending;
        } else {
          col.isSortedDescending = false;
        }

        return col;
      })
    );
  };

  const tagReplacementColumn: IColumn = {
    key: "tagReplacement",
    name: "Substituição",
    minWidth: 140,
    onRender: item => {
      return (
        <TagReplacementDialog
          item={item}
          onSuccess={() => props.refetchContractData()}
          businessUnitId={props.businessUnitId}
          service={props.service}
          disabled={
            serviceDeliveredStepOrder === undefined ||
            currentStep.order <= serviceDeliveredStepOrder ||
            [
              ContractStatus.TIMED_OUT,
              ContractStatus.REJECTED,
              ContractStatus.CANCELED
            ].includes(currentStep.internalName)
          }
        />
      );
    }
  };

  const handleOpenLinkInventoryModal = (item: ContractItem) => {
    setSelectedItemForInventoryModal(item);
    setIsLinkInventoryModalOpen(true);
  };

  const handleCloseLinkInventoryModal = () => {
    setIsLinkInventoryModalOpen(false);
    setSelectedItemForInventoryModal(null);
  };

  const handleOpenDetachInventoryModal = (item: ContractItem) => {
    setSelectedItemForInventoryModal(item);
    setIsDetachInventoryModalOpen(true);
  };

  const handleCloseDetachInventoryModal = () => {
    setIsDetachInventoryModalOpen(false);
    setSelectedItemForInventoryModal(null);
  };

  const handleOpenLineAdditionalServicesModal = (item: ContractItem) => {
    if (!item.line?.number) return;

    const searchParams = new URLSearchParams(locationSearchRef.current);

    searchParams.set(
      LINE_ADDITIONAL_SERVICES_LINE_NUMBER_PARAM,
      item.line.number
    );

    navigate({ search: searchParams.toString() });
  };

  const handleCloseLineAdditionalServicesModal = () => {
    const searchParams = new URLSearchParams(locationSearchRef.current);

    searchParams.delete(LINE_ADDITIONAL_SERVICES_LINE_NUMBER_PARAM);

    navigate({ search: searchParams.toString() });
  };

  const handleConfirmLinkInventory = async (serialNumber: string) => {
    if (!selectedItemForInventoryModal) return;

    const body = { serialNumber };
    await execute({
      url: `contracts/items/${selectedItemForInventoryModal.id}/attach-inventory`,
      method: "POST",
      bodyObj: body,
      showAlert: true,
      messages: {
        success: "Inventário vinculado com sucesso!",
        error: "Erro ao vincular inventário."
      }
    });

    await props.refetchContractData();
  };

  const itemCanShowInventoryAction = (item: ContractItem): boolean => {
    return !item.deletedAt;
  };

  const isMobileLineService =
    props.serviceCategoryType === ServicesCategoriesTypesEnum.MOBILE_LINE;

  const isLineService =
    props.serviceCategoryType &&
    [
      ServicesCategoriesTypesEnum.MOBILE_LINE,
      ServicesCategoriesTypesEnum.MOBILE_LINE_PRE
    ].includes(props.serviceCategoryType);

  const isBeforeProductDelivered =
    props.nextStep &&
    productDeliveredStepOrder !== undefined &&
    props.nextStep.order <= productDeliveredStepOrder;

  const shouldShowLinkInventoryAction =
    isMobileLineService && isBeforeProductDelivered;

  const canShowLineAdditionalServicesAction =
    isLineService && currentStep?.internalName === ContractStatus.DONE;

  const shouldShowActionColumn =
    shouldShowLinkInventoryAction || canShowLineAdditionalServicesAction;

  const actionConfig: ActionColumnConfig = shouldShowActionColumn
    ? {
        menuItems: item => {
          const menuItems: IContextualMenuItem[] = [];

          if (shouldShowLinkInventoryAction) {
            if (!item.inventory.length) {
              menuItems.push({
                key: "linkInventory",
                text: "Vincular Inventário",
                onClick: () => handleOpenLinkInventoryModal(item)
              });
            } else {
              menuItems.push({
                key: "detachInventory",
                text: "Desvincular Inventário",
                onClick: () => handleOpenDetachInventoryModal(item)
              });
            }
          }

          if (canShowLineAdditionalServicesAction) {
            menuItems.push({
              key: "lineAdditionalServices",
              text: "Serviços adicionais da linha",
              onClick: () => handleOpenLineAdditionalServicesModal(item)
            });
          }

          return menuItems;
        },
        shouldRender: itemCanShowInventoryAction
      }
    : null;

  useEffect(() => {
    if (columns) {
      const newColumns = buildColumns({
        isCompactMode,
        onColumnClick,
        serviceCategoryType: props.serviceCategoryType,
        actionConfig,
        isSimExempt
      });

      if (serviceCategoryIsTag && isEditingEnabled) {
        newColumns.push(tagReplacementColumn);
      }

      setColumns(newColumns);
      refColumns.current = newColumns;
    }
    //eslint-disable-next-line
  }, [isCompactMode, isSimExempt]);

  useEffect(() => {
    const defaultColumns = buildColumns({
      isCompactMode,
      onColumnClick,
      serviceCategoryType: props.serviceCategoryType,
      actionConfig,
      isSimExempt
    });

    if (serviceCategoryIsTag && isEditingEnabled) {
      defaultColumns.push(tagReplacementColumn);
    }

    setColumns(defaultColumns);
    refColumns.current = defaultColumns;
    //eslint-disable-next-line
  }, []);

  let tagsWithoutCanceled = [];

  if (serviceCategoryIsTag) {
    tagsWithoutCanceled = items.filter(
      item => !item.inventory[0]?.canceledAtStartag
    );
  }

  return (
    <Stack>
      <Stack
        tokens={{ padding: 10 }}
        horizontal
        horizontalAlign="space-between"
      >
        <Text variant={"xLarge"}>
          <Icon iconName="ProductVariant" style={{ marginRight: 10 }} />
          {items?.length === 1 ? "Benefício combo" : "Benefícios combos"}
        </Text>

        {isEditingEnabled && serviceCategoryIsTag && (
          <PrimaryButton
            id="migrate-plans-button"
            disabled={
              tagsWithoutCanceled.length === 0 ||
              serviceDeliveredStepOrder === undefined ||
              currentStep.order < serviceDeliveredStepOrder ||
              ["TIMED_OUT", "REJECTED", "CANCELED"].includes(
                currentStep.internalName
              )
            }
            onClick={() => props.onOpenRenewalProductsModal()}
          >
            Migrar planos de benefícios
          </PrimaryButton>
        )}
      </Stack>
      <Card borderRadius="1rem" padding="1rem">
        <Stack
          tokens={{ padding: 20 }}
          horizontal
          horizontalAlign="space-between"
        >
          <Toggle
            data-testid="toggle-view-mode"
            label="Exibir em modo compacto"
            checked={isCompactMode}
            onChange={(_, checked) => setIsCompactMode(checked)}
            onText="Compacto"
            offText="Normal"
          />
        </Stack>

        <DetailsList
          selectionMode={SelectionMode.none}
          constrainMode={ConstrainMode.horizontalConstrained}
          items={items}
          columns={columns}
          styles={listStyles}
          onRenderRow={rowProps => (
            <AccordionContractDetailRow
              rowProps={rowProps}
              isCompactMode={isCompactMode}
              isSimExempt={isSimExempt}
              items={items}
              refetchContractData={props.refetchContractData}
            />
          )}
        />
      </Card>

      <LinkInventoryModal
        isOpen={isLinkInventoryModalOpen}
        onDismiss={handleCloseLinkInventoryModal}
        onConfirm={handleConfirmLinkInventory}
        contractItemId={selectedItemForInventoryModal?.id}
      />

      <DetachInventoryModal
        isOpen={isDetachInventoryModalOpen}
        onDismiss={handleCloseDetachInventoryModal}
        item={selectedItemForInventoryModal}
        onSuccess={props.refetchContractData}
      />

      <LineAdditionalServicesModal
        isOpen={isLineAdditionalServicesModalOpen}
        onDismiss={handleCloseLineAdditionalServicesModal}
        item={selectedItemForLineAdditionalServices}
        contractId={props.contract.id}
      />
    </Stack>
  );
}
